import React, { useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  Grid,
  LinearProgress,
  Chip,
  Alert,
  Card,
  CardContent,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button
} from '@mui/material';
import {
  CheckCircle as CheckIcon,
  Warning as WarningIcon,
  Brightness5 as BrightnessIcon,
  Filter as SharpnessIcon,
  Contrast as ContrastIcon,
  CenterFocusStrong as CenterIcon,
  Share as ShareIcon,
  Visibility as FrameShapeIcon
} from '@mui/icons-material';

export default function QualityResults({ analysisData }) {
  const [infoDialogOpen, setInfoDialogOpen] = useState(false);

  if (!analysisData) {
    return null;
  }

  const { qualityScores, centering, socialMediaReady, recommendations, detectedLabels, frameShape } = analysisData;

  // Ensure checks object exists
  const checks = socialMediaReady?.checks || {};
  const hasChecks = Object.keys(checks).length > 0;

  const getScoreColor = (score, min = 50, max = 80) => {
    if (score >= min && score <= max) return 'success';
    if (score < min) return 'error';
    return 'warning';
  };

  const getProgressColor = (score, min = 50, max = 80) => {
    if (score >= min && score <= max) return 'success';
    if (score < min) return 'error';
    return 'warning';
  };

  const handleOpenInfoDialog = () => {
    setInfoDialogOpen(true);
  };

  const handleCloseInfoDialog = () => {
    setInfoDialogOpen(false);
  };

  return (
    <Paper elevation={3} sx={{ p: 3, mt: 3 }}>
      <Typography variant="h6" gutterBottom>
        Quality Analysis Results
      </Typography>

      {/* Social Media Readiness Banner */}
      <Alert
        severity={socialMediaReady.ready ? 'success' : 'warning'}
        icon={socialMediaReady.ready ? <CheckIcon /> : <WarningIcon />}
        sx={{ 
          mb: 3,
          ...(!socialMediaReady.ready && { 
            cursor: 'pointer',
            '&:hover': { bgcolor: 'warning.light' }
          })
        }}
        onClick={!socialMediaReady.ready ? handleOpenInfoDialog : undefined}
      >
        <Typography variant="subtitle1" fontWeight="bold">
          {socialMediaReady.message}
        </Typography>
        {hasChecks && (
          <Typography variant="body2">
            Overall Score: {socialMediaReady.score}% ({Object.values(checks).filter(v => v).length}/{Object.keys(checks).length} checks passed)
          </Typography>
        )}
        {!hasChecks && (
          <Typography variant="body2">
            Overall Score: {socialMediaReady.score}%
          </Typography>
        )}
      </Alert>



      {/* Frame Shape Detection */}
      <Box sx={{ mb: 3 }}>
        <Divider sx={{ mb: 2 }} />
        <Typography variant="h6" gutterBottom display="flex" alignItems="center">
          <FrameShapeIcon sx={{ mr: 1 }} />
          Frame Shape
        </Typography>
        {frameShape && frameShape.detected ? (
          <Box display="flex" alignItems="center" gap={2} flexWrap="wrap">
            <Chip
              label={frameShape.shape}
              color="primary"
              size="medium"
              sx={{ fontWeight: 'bold', fontSize: '0.95rem' }}
            />
            <Typography variant="body2" color="text.secondary">
              Confidence: {frameShape.confidence}%
            </Typography>
            {frameShape.allLabels && frameShape.allLabels.length > 1 && (
              <Box display="flex" gap={1} flexWrap="wrap">
                {frameShape.allLabels
                  .filter(l => l.name !== frameShape.shape)
                  .map((l, i) => (
                    <Chip
                      key={i}
                      label={`${l.name} (${l.confidence}%)`}
                      variant="outlined"
                      size="small"
                      color="default"
                    />
                  ))}
              </Box>
            )}
          </Box>
        ) : (
          <Chip label="Detection pending" color="warning" variant="outlined" size="small" />
        )}
      </Box>

      {/* Recommendations */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="h6" gutterBottom display="flex" alignItems="center">
          <ShareIcon sx={{ mr: 1 }} />
          Recommendations
        </Typography>
        <List>
          {recommendations.map((rec, index) => {
            const isSuccess = rec.startsWith('✅');
            const isWarning = rec.startsWith('⚠️');
            return (
              <ListItem key={index} divider={index < recommendations.length - 1}>
                <ListItemIcon>
                  {isSuccess ? (
                    <CheckIcon color="success" />
                  ) : isWarning ? (
                    <WarningIcon color="warning" />
                  ) : (
                    <WarningIcon color="error" />
                  )}
                </ListItemIcon>
                <ListItemText
                  primary={rec.replace(/^[✅⚠️]\s*/, '')}
                  primaryTypographyProps={{
                    color: isSuccess ? 'success.main' : isWarning ? 'warning.main' : 'error.main'
                  }}
                />
              </ListItem>
            );
          })}
        </List>
      </Box>

      {/* Detected Labels */}
      {detectedLabels && detectedLabels.length > 0 && (
        <Box>
          <Divider sx={{ mb: 2 }} />
          <Typography variant="h6" gutterBottom>
            Detected Objects
          </Typography>
          <Box display="flex" flexWrap="wrap" gap={1}>
            {detectedLabels.map((label, index) => (
              <Chip
                key={index}
                label={label.name}
                variant="outlined"
                size="small"
              />
            ))}
          </Box>
        </Box>
      )}

      {/* Social Media Readiness Info Dialog */}
      <Dialog
        open={infoDialogOpen}
        onClose={handleCloseInfoDialog}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <ShareIcon color="primary" />
            Social Media Readiness Criteria
          </Box>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1" paragraph>
            An image is considered <strong>"Social Media Ready"</strong> when it passes all of the following quality checks:
          </Typography>

          <Paper variant="outlined" sx={{ p: 2, mb: 2, bgcolor: '#f5f5f5' }}>
            <Typography variant="subtitle2" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <CheckIcon fontSize="small" color="success" />
              1. Brightness Level
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ ml: 3 }}>
              Must be between <strong>60-80</strong> (not too dark or overexposed)
            </Typography>
          </Paper>

          <Paper variant="outlined" sx={{ p: 2, mb: 2, bgcolor: '#f5f5f5' }}>
            <Typography variant="subtitle2" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <CheckIcon fontSize="small" color="success" />
              2. Sharpness
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ ml: 3 }}>
              Must be at least <strong>70</strong> (clear and not blurry)
            </Typography>
          </Paper>

          <Paper variant="outlined" sx={{ p: 2, mb: 2, bgcolor: '#f5f5f5' }}>
            <Typography variant="subtitle2" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <CheckIcon fontSize="small" color="success" />
              3. Contrast
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ ml: 3 }}>
              Must be at least <strong>50</strong> (good separation between subject and background)
            </Typography>
          </Paper>

          <Paper variant="outlined" sx={{ p: 2, mb: 2, bgcolor: '#f5f5f5' }}>
            <Typography variant="subtitle2" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <CheckIcon fontSize="small" color="success" />
              4. Product Positioning
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ ml: 3 }}>
              Product must be <strong>centered</strong> (within 15% tolerance) and take up <strong>appropriate space</strong> (9-49% of frame)
            </Typography>
          </Paper>

          <Alert severity="info" sx={{ mt: 3 }}>
            <Typography variant="body2">
              <strong>Overall Score:</strong> The percentage shown is calculated as (passed checks / total checks × 100).
              All 4 checks must pass for an image to be marked as "Social Ready".
            </Typography>
          </Alert>

          <Alert severity="warning" sx={{ mt: 2 }}>
            <Typography variant="body2">
              Images that are "Not Ready" can still be used, but may require editing or retaking for optimal social media presentation.
            </Typography>
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseInfoDialog} variant="contained">
            Got it
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
}
